import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from '../../shared/Button'
import Text from '../../shared/Text'
import styles from '../../../utility/styles'

/**
 * Formats a user's name for display in the DefaultAvatar
 * Takes the first two names (separated by spaces) and capitalizes the first letter of each
 * If there's only one name, shows just the first letter capitalized
 * @param {string} name - The user's full name
 * @returns {string} - Formatted initials (e.g., "JD" for "John Doe", "J" for "John")
 */
const formatUserNameForAvatar = (name) => {
	try {
		if (!name || typeof name !== 'string') {
			return '🤷';
		}

		const nameParts = name.trim().split(' ').filter(part => part.length > 0);

		if (nameParts.length === 0) {
			return '🤷';
		}

		if (nameParts.length === 1) {
			// Single name - return first letter capitalized
			return nameParts[0].charAt(0).toUpperCase();
		}

		// Multiple names - return first letter of first two names
		const firstInitial = nameParts[0].charAt(0).toUpperCase();
		const secondInitial = nameParts[1].charAt(0).toUpperCase();

		return firstInitial + secondInitial;
	} catch (error) {
		return '🤷';
	}
};

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${styles.functions.opacityModifier(
	styles.colors.background,
	80
)};
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	padding: 20px;
`

const ModalContent = styled.div`
	background-color: ${styles.colors.backgroundDeep};
	border-radius: 16px;
	padding: 32px;
	max-width: 600px;
	width: 100%;
	max-height: calc(80vh / var(--dash-scale, 1));
	overflow-y: auto;
	box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`

const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10));
`

const CloseButton = styled.button`
	background: none;
	border: none;
	color: ${styles.colors.mediumGray};
	font-size: 24px;
	cursor: pointer;
	padding: 8px;
	border-radius: 50%;
	transition: all 0.2s ease;
	
	&:hover {
		background-color: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
		color: ${styles.colors.white};
	}
`

const LeaderboardList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const LeaderboardItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	background-color: ${styles.colors.background};
	border-radius: 12px;
	border: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10));
	transition: all 0.2s ease;
	
	&:hover {
		border-color: ${styles.colors.blue};
		transform: translateY(-2px);
	}
`

const UserInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`

const UserAvatar = styled.img`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	object-fit: cover;
`

const DefaultAvatar = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
	border: 2px solid ${styles.colors.blue};
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${styles.colors.white};
	font-weight: 600;
	font-size: 16px;
`

const UserName = styled(Text.Body)`
	font-weight: 600;
	color: ${styles.colors.white};
	margin: 0;
`

const DistanceInfo = styled.div`
	text-align: right;
`

const DistanceValue = styled(Text.Body)`
	font-weight: 700;
	color: ${styles.colors.blue};
	margin: 0;
	font-size: 18px;
`

const DistanceUnits = styled(Text.Body)`
	color: ${styles.colors.mediumGray};
	margin: 0;
	font-size: 14px;
`

const EmptyState = styled.div`
	text-align: center;
	padding: 40px 20px;
	color: ${styles.colors.mediumGray};
`

const LoadingState = styled.div`
	text-align: center;
	padding: 40px 20px;
`

const FilterSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 24px;
	padding: 20px;
	background-color: ${styles.colors.background};
	border-radius: 12px;
	border: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10));
`

const FilterRow = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
	flex-wrap: wrap;
`

const FilterGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 120px;
`

const FilterLabel = styled(Text.Body)`
	color: ${styles.colors.mediumGray};
	font-size: 12px;
	font-weight: 500;
	margin: 0;
`

const FilterDropdown = styled.div`
	position: relative;
	min-width: 120px;
`

const FilterButton = styled.button`
	background-color: ${styles.colors.backgroundDeep};
	border: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10));
	border-radius: 8px;
	padding: 8px 12px;
	color: ${styles.colors.white};
	font-size: 14px;
	cursor: pointer;
	transition: all 0.2s ease;
	width: 100%;
	text-align: left;
	display: flex;
	justify-content: space-between;
	align-items: center;
	touch-action: manipulation;
	-webkit-tap-highlight-color: transparent;
	
	&:hover {
		border-color: ${styles.colors.blue};
	}
	
	&:active {
		transform: scale(0.98);
	}
`

const DropdownArrow = styled.span`
	transition: transform 0.2s ease;
	transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const DropdownMenu = styled.div`
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background-color: ${styles.colors.backgroundDeep};
	border: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10));
	border-radius: 8px;
	margin-top: 4px;
	z-index: 10;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
	overflow: hidden;
	opacity: ${props => props.isOpen ? 1 : 0};
	visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
	transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
	transition: all 0.2s ease;
`

const DropdownOption = styled.button`
	background: none;
	border: none;
	color: ${styles.colors.white};
	font-size: 14px;
	padding: 8px 12px;
	cursor: pointer;
	width: 100%;
	text-align: left;
	transition: background-color 0.2s ease;
	
	&:hover {
		background-color: ${styles.colors.background};
	}
	
	&:active {
		background-color: ${styles.colors.blue};
	}
`

const ActionRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	align-items: center;
`

const UpdateButton = styled(Button)`
	background-color: ${styles.colors.blue};
	color: ${styles.colors.white};
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 600;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	
	&:hover {
		background-color: ${styles.functions.opacityModifier(styles.colors.blue, 80)};
		transform: translateY(-1px);
	}
	
	&:disabled {
		background-color: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
		color: ${styles.colors.mediumGray};
		cursor: not-allowed;
		transform: none;
	}
`

const ChangesIndicator = styled(Text.Body)`
	color: ${styles.colors.orange};
	font-size: 12px;
	font-weight: 500;
	margin: 0;
`

/**
 * LeaderBoardModal component
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   type: 'distance' | 'duration',
 *   period: 'all_time' | 'days7' | 'days30',
 *   autopilot_type: 'fsd' | 'autopilot',
 *   leaderboardData: Array<{
 *     user: {
 *       id: number,
 *       name: string,
 *       image: string,
 *       model_name: string,
 *       private_profile: boolean,
 *       pro_user: boolean
 *     },
 *     drive_distance: number,
 *     drive_distance_units: string,
 *     drive_distance_string: string | undefined,
 *     drive_time_string: string | undefined,
 *     drive_time_s: number | undefined,
 *   }>,
 *   isLoading?: boolean,
 *   onTypeChange?: (type: string) => void,
 *   onPeriodChange?: (period: string) => void,
 *   onAutopilotTypeChange?: (autopilot_type: string) => void,
 *   onFetchData?: (type: string, period: string, autopilot_type: string) => void
 * }} props
 */
export default function LeaderBoardModal({
	isOpen,
	onClose,
	type = 'distance',
	period = 'days7',
	autopilot_type = 'fsd',
	leaderboardData = [],
	isLoading = false,
	onTypeChange,
	onPeriodChange,
	onAutopilotTypeChange,
	onFetchData
}) {
	// State for dropdown open/close
	const [openDropdown, setOpenDropdown] = useState(null);

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleDropdownToggle = (dropdownName) => {
		setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
	};

	const handleOptionSelect = (dropdownName, value) => {
		// Calculate the new values based on what's being changed
		let newType = type;
		let newPeriod = period;
		let newAutopilotType = autopilot_type;

		switch (dropdownName) {
			case 'type':
				newType = value;
				if (onTypeChange) onTypeChange(value);
				break;
			case 'period':
				newPeriod = value;
				if (onPeriodChange) onPeriodChange(value);
				break;
			case 'autopilot':
				newAutopilotType = value;
				if (onAutopilotTypeChange) onAutopilotTypeChange(value);
				break;
		}
		setOpenDropdown(null);

		// Automatically trigger data fetch with the new values
		setTimeout(() => {
			if (onFetchData) {
				onFetchData(newType, newPeriod, newAutopilotType);
			}
		}, 100);
	};



	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (openDropdown && !event.target.closest('.filter-dropdown')) {
				setOpenDropdown(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('touchstart', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
		};
	}, [openDropdown]);

	// Helper functions to get display labels
	const getTypeLabel = (value) => {
		switch (value) {
			case 'distance': return 'Distance';
			case 'duration': return 'Duration';
			default: return value;
		}
	};

	const getPeriodLabel = (value) => {
		switch (value) {
			case 'days7': return 'Last 7 Days';
			case 'days30': return 'Last 30 Days';
			case 'all_time': return 'All Time';
			default: return value;
		}
	};

	const getAutopilotLabel = (value) => {
		switch (value) {
			case 'fsd': return 'FSD';
			case 'autopilot': return 'Autopilot';
			default: return value;
		}
	};

	if (!isOpen) return null;

	return (
		<ModalOverlay onClick={handleOverlayClick}>
			<ModalContent>
				<ModalHeader>
					<div>
						<Text.Heading type="h3" color={styles.colors.white}>
							Leaderboard
						</Text.Heading>
						<Text.Body color={styles.colors.mediumGray}>
							Greatest {type === 'distance' ? 'Distance' : 'Duration'} {period === 'days7' ? 'in the last 7 days' : period === 'all_time' ? 'all time' : 'in the last 30 days'} {autopilot_type === 'fsd' ? 'with FSD' : 'with Autopilot'}
						</Text.Body>
					</div>
					<CloseButton onClick={onClose}>
						×
					</CloseButton>
				</ModalHeader>

				<FilterSection>
					<FilterRow>
						<FilterGroup>
							<FilterLabel>Type</FilterLabel>
							<FilterDropdown className="filter-dropdown">
								<FilterButton
									onClick={() => handleDropdownToggle('type')}
									type="button"
								>
									{getTypeLabel(type)}
									<DropdownArrow isOpen={openDropdown === 'type'}>▼</DropdownArrow>
								</FilterButton>
								<DropdownMenu isOpen={openDropdown === 'type'}>
									<DropdownOption
										onClick={() => handleOptionSelect('type', 'distance')}
										type="button"
									>
										Distance
									</DropdownOption>
									<DropdownOption
										onClick={() => handleOptionSelect('type', 'duration')}
										type="button"
									>
										Duration
									</DropdownOption>
								</DropdownMenu>
							</FilterDropdown>
						</FilterGroup>

						<FilterGroup>
							<FilterLabel>Period</FilterLabel>
							<FilterDropdown className="filter-dropdown">
								<FilterButton
									onClick={() => handleDropdownToggle('period')}
									type="button"
								>
									{getPeriodLabel(period)}
									<DropdownArrow isOpen={openDropdown === 'period'}>▼</DropdownArrow>
								</FilterButton>
								<DropdownMenu isOpen={openDropdown === 'period'}>
									<DropdownOption
										onClick={() => handleOptionSelect('period', 'days7')}
										type="button"
									>
										Last 7 Days
									</DropdownOption>
									<DropdownOption
										onClick={() => handleOptionSelect('period', 'days30')}
										type="button"
									>
										Last 30 Days
									</DropdownOption>
									<DropdownOption
										onClick={() => handleOptionSelect('period', 'all_time')}
										type="button"
									>
										All Time
									</DropdownOption>
								</DropdownMenu>
							</FilterDropdown>
						</FilterGroup>

						<FilterGroup>
							<FilterLabel>Autopilot Type</FilterLabel>
							<FilterDropdown className="filter-dropdown">
								<FilterButton
									onClick={() => handleDropdownToggle('autopilot')}
									type="button"
								>
									{getAutopilotLabel(autopilot_type)}
									<DropdownArrow isOpen={openDropdown === 'autopilot'}>▼</DropdownArrow>
								</FilterButton>
								<DropdownMenu isOpen={openDropdown === 'autopilot'}>
									<DropdownOption
										onClick={() => handleOptionSelect('autopilot', 'fsd')}
										type="button"
									>
										FSD
									</DropdownOption>
									<DropdownOption
										onClick={() => handleOptionSelect('autopilot', 'autopilot')}
										type="button"
									>
										Autopilot
									</DropdownOption>
								</DropdownMenu>
							</FilterDropdown>
						</FilterGroup>
					</FilterRow>

					{isLoading && (
						<ActionRow>
							<ChangesIndicator>
								Updating data...
							</ChangesIndicator>
						</ActionRow>
					)}
				</FilterSection>

				{isLoading ? (
					<LoadingState>
						<Text.Body color={styles.colors.mediumGray}>
							Loading leaderboard data...
						</Text.Body>
					</LoadingState>
				) : leaderboardData.length === 0 ? (
					<EmptyState>
						<Text.Body color={styles.colors.mediumGray}>
							No leaderboard data available
						</Text.Body>
					</EmptyState>
				) : (
					<LeaderboardList>
						{leaderboardData.map((entry, index) => (
							<LeaderboardItem key={entry.user.id}>
								<UserInfo>
									{entry.user.image ? (
										<UserAvatar
											src={entry.user.image}
											alt={entry.user.name}
											onError={(e) => {
												e.target.style.display = 'none';
												e.target.nextSibling.style.display = 'flex';
											}}
										/>
									) : null}
									<DefaultAvatar style={{ display: entry.user.image ? 'none' : 'flex' }}>
										{formatUserNameForAvatar(entry.user.name)}
									</DefaultAvatar>
									<div>
										<UserName>
											{entry.user.name}
										</UserName>
										{entry.user.model_name && (
											<Text.Body
												color={styles.colors.mediumGray}
												style={{ fontSize: '12px', margin: 0 }}
											>
												{entry.user.model_name}
											</Text.Body>
										)}
									</div>
								</UserInfo>
								<DistanceInfo>
									<DistanceValue>
										{entry.drive_distance_string || entry.drive_time_string}
									</DistanceValue>
								</DistanceInfo>
							</LeaderboardItem>
						))}
					</LeaderboardList>
				)}
			</ModalContent>
		</ModalOverlay>
	);
}
